import { Layout } from "providers/Layout";
import { LayoutHeader } from "./components/LayoutHeader";
import { PropertiesTable } from "views/properties/components/PropertiesTable";
import { keepPreviousData } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import addressApi, { QUERY_GET_PROPERTIES } from "api/landlord/address";
import { DeletePropertyModal } from "shared/app/DeletePropertyModal";
import { Pagination } from "shared/components/Pagination";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { PropertyGroupSettings } from "./components/PropertyGroupSettings";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { useParams } from "react-router-dom";
import { instance } from "api/apiBase";
import { Loader } from "shared/components/Loader";
import { PropertyGroupSplitBy } from "views/property-groups-configuration/types";

const DEFAULT_PER_PAGE = Number(window.localStorage.getItem("per_page")) || 10;

export const PropertyGroupDetail = () => {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [selectedPropertyId] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [showReviewModal, setShowReviewModal] = useState(false);

  const { isLoading: isLoadingPropertyGroupConfig } = useQuery({
    queryKey: ["property_group_config", id],
    queryFn: () =>
      instance.get(`/portal/v1/property_groups/config`).then((res) => res.data),
    enabled: !!id
  });

  const { data: propertyGroup, isLoading: isLoadingPropertyGroup } = useQuery({
    queryKey: ["property_group", id],
    queryFn: () =>
      instance.get(`/portal/v1/property_groups/${id}`).then((res) => {
        return res.data;
      }),
    enabled: !!id
  });

  useEffect(() => {
    if (propertyGroup && propertyGroup.verification_template?.id)
      setSelectedTemplateId(propertyGroup.verification_template.id);
  }, [propertyGroup]);

  const { data, refetch, isFetching } = useQuery({
    queryKey: [QUERY_GET_PROPERTIES, page],
    queryFn: () =>
      instance
        .get(`/portal/v1/property_groups/${id}/properties`, {
          params: {
            page,
            per_page: DEFAULT_PER_PAGE
          }
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData
  });

  const getSplitByOptions = () => {
    if (
      !propertyGroup?.split_data_chain ||
      !Array.isArray(propertyGroup.split_data_chain) ||
      propertyGroup.split_data_chain.length <= 1
    )
      return [];

    return propertyGroup.split_data_chain
      .slice(1)
      .map((item) => item.split_type)
      .filter((type) => type) as PropertyGroupSplitBy[];
  };

  const deleteProperty = async () => {
    try {
      await addressApi.deleteProperty(selectedPropertyId || "");
      refetch();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
    setShowDeletePropertyModal(false);
  };

  if (isLoadingPropertyGroup || isLoadingPropertyGroupConfig)
    return (
      <div className="flex justify-center items-center h-[100vh]">
        <Loader />
      </div>
    );

  const splitByOptions = getSplitByOptions();

  return (
    <Layout
      sectionClassName="space-y-4"
      headerClassName="p-0"
      header={
        <LayoutHeader
          onSettingsClick={() => setShowSettings(!showSettings)}
          propertyGroup={propertyGroup}
          selectedSplitByOptions={splitByOptions}
        />
      }
    >
      <PropertiesTable
        showPropertyGroup={false}
        data={data?.items || []}
        setSelectedPropertyId={() => {}}
        setShowDeletePropertyModal={() => {}}
        isLoading={isFetching}
        perPageRows={DEFAULT_PER_PAGE}
        pagination={
          <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
            <Pagination
              className="md:justify-end justify-center"
              total={data?.page_count || 0}
              current={page}
              onChange={(p) => setPage(p)}
            />
          </div>
        }
        viewPortHeightOffset={20}
      />
      <DeletePropertyModal
        opened={showDeletePropertyModal}
        onClose={() => setShowDeletePropertyModal(false)}
        propertyId={selectedPropertyId || ""}
        onSuccess={() => refetch()}
      />

      <PropertyGroupSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
        onShowTemplateReviewModal={() => setShowReviewModal(true)}
        setSelectedTemplateId={setSelectedTemplateId}
        selectedTemplateId={selectedTemplateId}
        propertyGroup={propertyGroup}
      />
      <Suspense fallback={null}>
        <TemplateReviewModal
          opened={
            showReviewModal && !!selectedTemplateId && selectedTemplateId !== id
          }
          onClose={() => setShowReviewModal(false)}
          id={selectedTemplateId || ""}
        />
      </Suspense>
    </Layout>
  );
};
